import {AvalancheReelsView} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsView';
import {
	ObjectSize,
	Offset,
	Position,
	ReelConstants,
	Scale,
	SymbolPosition,
} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {AvalancheReel} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReel';
import {GameAvalancheReel} from 'src/avalanche/GameAvalancheReel';
import {AvalancheNewSymbol} from '@fantasmagames/slot/src/logic/SpinResult';
import {WinLine} from '@fantasmagames/slot/src/logic/WinLine';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {GameReelConstants} from 'src/reels/GameReelConstants';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {PrototypeWinLine} from '@fantasmagames/slot/src/logic/prototype/PrototypeSpinResult';
import {GamePrototypeWinLine} from 'src/logic/prototype/GamePrototypeLogic';
import {Symbols} from 'src/reels/Symbols';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {BackgroundView} from '@fantasmagames/slot/src/modules/background/BackgroundView';
import {TimingConstants} from 'src/timing/TimingConstants';

export class GameAvalancheReelsView extends AvalancheReelsView {
	public static readonly INITIAL_WIN_INDICATION_FINISHED: string = 'GameAvalancheReelsView::INITIAL_WIN_INDICATION_FINISHED';
	public static readonly INITIAL_WIN_INDICATION_STARTED: string = 'GameAvalancheReelsView::INITIAL_WIN_INDICATION_STARTED';
	public static readonly INITIAL_WIN_INDICATION_WINLINE: string = 'GameAvalancheReelsView::INITIAL_WIN_INDICATION_WINLINE';
	public static readonly SYMBOL_PASSED_THROUGHT_LINE: string = 'GameAvalancheReelsView::SYMBOL_PASSED_THROUGHT_LINE';
	public static readonly SCATTER_ATTENTION_TRIGGERED: string = 'GameAvalancheReelsView::SCATTER_ATTENTION_TRIGGERED';
	public static readonly RESPIN_ATTENTION_TRIGGERED: string = 'GameAvalancheReelsView::RESPIN_ATTENTION_TRIGGERED';
	public static readonly FILLING_VOIDS_STARTED: string = 'GameAvalancheReelsView::FILL_VOIDS_STARTED';

	protected _currentScatterIndex: number = 0;
	protected _reelsWithScatters: number[] = [];
	protected _oversizedLayer: PIXI.Container;
	protected _reelsMask: PIXI.Container;

	protected createReel(reelIndex: number, startReelPosition: number, offsets: SymbolPosition, container: PIXI.Sprite): AvalancheReel {
		return new GameAvalancheReel(reelIndex, startReelPosition, offsets, container);
	}

	public removeSymbolsOnAvalancheWithSound(symbols: SymbolPosition[], win: number, bet: number): void {

		let delay: number = 0;
		let lastReelWithRemovedSymbols: number = 0;

		symbols.forEach((symPos: SymbolPosition) => {
			if (symPos.x > lastReelWithRemovedSymbols) {
				lastReelWithRemovedSymbols = symPos.x;
			}
		});

		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			const symbolsOnReel: SymbolPosition[] = this.getAllSymbolPositionsFromReel(reelIndex, symbols);
			const gameSymbols: GameSymbol[] = this.makeSizedGameSymbolsFromSymbols(symbolsOnReel);
			if (symbolsOnReel.length) {
				this.timeUtils.createTimer(() => {
					this._reels[reelIndex].removeAvalancheSymbols(symbolsOnReel, reelIndex === lastReelWithRemovedSymbols ? this.onSymbolsRemovingFinshed.bind(this) : null);
					this.playAvalancheDestroyAnimationFor(gameSymbols, null);
				}, delay);
				delay += TimingConstants.SYMBOL_REMOVE_STAGGER_DELAY;
			}
		}
	}

	protected playAvalancheDestroyAnimationFor(gameSymbolsForAnimation: GameSymbol[], onFinishedCallback: Function): void {
		let isLast: boolean = false;
		let index = 0;

		for (const sym of gameSymbolsForAnimation) {
			if (index === gameSymbolsForAnimation.length - 1) {
				isLast = true;
			}

			this.playAvalancheExplodeAnimationAt(sym, isLast ? () => {
				this.timeUtils.createTimer(() => {
					onFinishedCallback && onFinishedCallback(this.makeSymbolsPositionsFromGameSymbols(gameSymbolsForAnimation));
				}, 150);
			} : null);
			index++;
		}
	}

	protected makeSymbolsPositionsFromGameSymbols(gameSymbols: GameSymbol[]): SymbolPosition[] {
		const positions: SymbolPosition[] = [];

		for (const sym of gameSymbols) {
			positions.push({x: sym.position.x, y: sym.position.y});
		}

		return positions;
	}

	protected makeSizedGameSymbolsFromSymbols(symbols: SymbolPosition[]): GameSymbol[] {
		const gameSymbols: GameSymbol[] = [];

		for (const sym of symbols) {
			gameSymbols.push(
				new GameSymbol(
					(this._reels[sym.x] as GameAvalancheReel).getSymbolTypeOnReelIndex(sym.y),
					{width: 1, height: 1},
					{x: sym.x, y: sym.y},
				),
			);
		}

		return gameSymbols;
	}

	protected playAvalancheExplodeAnimationAt(sym: GameSymbol, onCompleted: Function): void {
		const posOnScreen: Position = GameReelConstants.getGameSymbolCenterPosition(sym.position);

		const animation: SpineAnimation = this.createSizedBlowAvalancheAnimation(posOnScreen, sym.sym);

		const isWildSymbol: boolean = sym.sym === Symbols.WR || sym.sym === Symbols.TOP_WR;
		!isWildSymbol && (animation.timeScale = 1.25);

		animation.setOnComplete(() => {
			onCompleted && onCompleted();
		});
	}

	protected createSizedBlowAvalancheAnimation(
		posOnScreen: Position,
		symbolNumber: number,
	): SpineAnimation {
		return this.spineUtils
			.startSpineAnimationCreation(this.getBlowAnimationSpineName(symbolNumber)
				, this.getBlowAnimationName(symbolNumber)
				, false)
			.container(this.container)
			.position(posOnScreen.x, posOnScreen.y)
			.createAndStart();
	}

	protected getBlowAnimationName(symbolNumber: number): string {
		const isWildSymbol: boolean = symbolNumber === Symbols.WR || symbolNumber === Symbols.TOP_WR;

		return isWildSymbol ? 'EXIT' : 'EXIT';
	}

	protected getBlowAnimationSpineName(symbolNumber: number): string {
		const isWildSymbol: boolean = symbolNumber === Symbols.WR || symbolNumber === Symbols.TOP_WR;

		return isWildSymbol ? 'WILD.json' : 'INITIAL_WIN.json';
	}

	/**
	 * adding symbols on speceific reel
	 * @param reelIndex
	 * @param symbols - symbols to add
	 * @param winLines - all the win lines
	 * @param currentReels - reels
	 * @param onExpectedWinAnimationCallback
	 * @param onNoWinAnimationCallback
	 * @param onSymbolFallenCallback
	 */
	protected addReelSymbols(
		reelIndex: number,
		symbols: AvalancheNewSymbol[],
		winLines: WinLine[],
		currentReels: GameSymbol[][],
		onExpectedWinAnimationCallback: Function,
		onNoWinAnimationCallback: Function,
		onSymbolFallenCallback: Function,
	): void {
		const reel: AvalancheReel = this._reels[reelIndex];

		const newSymbols: AvalancheNewSymbol[] = this.getAllSymbolsFromReel(reelIndex, symbols);

		reel.addNewSymbols(newSymbols, () => {
			onSymbolFallenCallback && onSymbolFallenCallback(reelIndex);
		});

		this.rearrangeSymbolsOrder();
		this.fixSymbolsVisibilityDuringAvalanches();

		onNoWinAnimationCallback && onNoWinAnimationCallback();
	}

	protected setupLayers(): void {
		this.container.children.length = 0;
		this._reels = [];

		this._reelsetContainer = new PIXI.Sprite();
		this._reelsMask = new PIXI.Container();
		this._oversizedLayer = new PIXI.Container();

		this._reelsMask.addChild(this._currentMask);
		this._reelsMask.mask = this._currentMask;

		this.container.addChild(this._reelsMask);
		this.container.addChild(this._oversizedLayer);

		this._reelsMask.addChild(this._reelsetContainer);
	}

	public createReelSet(reels: GameSymbol[][]): void {
		this.setupLayers();

		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			const offsets: Offset = {
				x: GameReelConstants.getReelsOffsetForRow(0).x + reelIndex * GameReelConstants.getTopSymbolSlot().width,
				y: GameReelConstants.getReelsOffsetForRow(0).y,
			};

			const reel: GameAvalancheReel = this.createReel(reelIndex, 0, offsets, this._reelsetContainer) as GameAvalancheReel;
			reel.setupReel(this.resources, this.soundPlayer);
			reel.timerUtils = this.timeUtils;
			reel.lineYPosition = this.layout.getAbsolutePosition('ROW_DIVIDER_03').y;

			if (reels) {
				reel.init(reels[reelIndex]);
			} else {
				reel.init();
			}

			reel.onReelDropOutFinished = this.onReelDropOutFinished.bind(this);
			reel.onReelDropFirstSymbolFinished = this.onReelFirstSymbolDropped.bind(this);
			reel.onReelDropNewFinished = this.onReelDropNewFinished.bind(this);
			reel.onFillingVoidsFinished = this.onFillingVoidsFinished.bind(this);
			reel.movingThroughTheLineCallback = this.onPassingThroughLine.bind(this);
			reel.respinAttentionTriggeredCallback = this.onRespinAttentionTriggered.bind(this);

			this.updateManager.add(reel);
			reel.start();
			this._reels.push(reel);
		}
		this._reels[ReelConstants.REELS_AMOUNT - 1].onReelDropOutFinished = this.onLastReelDropOutFinished.bind(this);
		this._reels[ReelConstants.REELS_AMOUNT - 1].onReelDropNewFinished = this.onLastReelDropNewFinished.bind(this);

		this.forceUpdateOrientation();

		this.setupInitialRowsToShow(ReelConstants.ROW_AMOUNT);

		this.setOversizedLayer();
	}

	protected onReelFirstSymbolDropped(reelNr: number): void {
		this.tryToPlayScatterAnimationOnReel(reelNr);
	}

	protected onFillingVoidsFinished(reelNr: number): void {
		super.onFillingVoidsFinished(reelNr);

		this.tryToPlayScatterAnimationOnReel(reelNr);
	}

	protected tryToPlayAnimationOverScatters(): void {
		for (let reel: number = 0; reel < ReelConstants.REELS_AMOUNT; reel++) {
			this.tryToPlayScatterAnimationOnReel(reel);
		}
	}

	protected tryToPlayScatterAnimationOnReel(reelIndex: number): void {
		if (!this.hasAlreadyPlayedScatterAttentionOnReel(reelIndex)) {
			for (let i = 3; i < GameReelConstants.ROW_AMOUNT; i++) {
				const symbolType: number = (this._reels[reelIndex] as GameAvalancheReel).getSymbolTypeOnReelIndex(i);
				if (symbolType === Symbols.TOP_BN || symbolType === Symbols.BN) {
					(this._reels[reelIndex] as GameAvalancheReel)
						.getSymbolFromPosition(i)
						.playScatterBonusLanding(++this._currentScatterIndex, this.updateScatterIdleAnimation.bind(this));
					this.emit(GameAvalancheReelsView.SCATTER_ATTENTION_TRIGGERED, this._currentScatterIndex);
					this._reelsWithScatters.push(reelIndex);
				}
			}
		}
	}

	protected updateScatterIdleAnimation(): void {
		for (let reelIndex = 0; reelIndex < GameReelConstants.REELS_AMOUNT; reelIndex++) {
			for (let rowIndex = 3; rowIndex < GameReelConstants.ROW_AMOUNT; rowIndex++) {
				const symbolType: number = (this._reels[reelIndex] as GameAvalancheReel).getSymbolTypeOnReelIndex(rowIndex);
				if (symbolType === Symbols.TOP_BN || symbolType === Symbols.BN) {
					(this._reels[reelIndex] as GameAvalancheReel)
						.getSymbolFromPosition(rowIndex)
						.playScatterBonusIdleAnimation(this._currentScatterIndex);
				}
			}
		}
	}

	public updateIdleAnimationOnRestore(): void {
		for (let reelIndex = 0; reelIndex < GameReelConstants.REELS_AMOUNT; reelIndex++) {
			for (let rowIndex = 3; rowIndex < GameReelConstants.ROW_AMOUNT; rowIndex++) {
				const symbolType: number = (this._reels[reelIndex] as GameAvalancheReel).getSymbolTypeOnReelIndex(rowIndex);
				const symbolFromReel = (this._reels[reelIndex] as GameAvalancheReel).getSymbolFromPosition(rowIndex);

				if (symbolType === Symbols.BN) {
					this._currentScatterIndex++;
					this._reelsWithScatters.push(reelIndex);

					this.updateScatterIdleAnimation();
				} else if (symbolFromReel.shouldHaveWildIdleAnimation() || symbolFromReel.shouldHaveRespinAttentionAnimation()) {
					symbolFromReel.addIdleAnimation();
				}
			}
		}
	}

	protected hasAlreadyPlayedScatterAttentionOnReel(reelIndex: number): boolean {
		return this._reelsWithScatters.indexOf(reelIndex) >= 0;
	}

	protected onPassingThroughLine(symbolID: number, newRowPos: number): void {
		this.emit(GameAvalancheReelsView.SYMBOL_PASSED_THROUGHT_LINE, symbolID, newRowPos);
	}

	protected onRespinAttentionTriggered(): void {
		this.emit(GameAvalancheReelsView.RESPIN_ATTENTION_TRIGGERED);
	}

	public removeScatterAt(symPos: SymbolPosition): void {
		(this._reels[symPos.x] as GameAvalancheReel).removeScatterRespinAs(symPos);
	}

	public removeRespinSymbol(symPos: SymbolPosition, symbol: number, finishedCallback: Function): void {
		(this._reels[symPos.x] as GameAvalancheReel).playRespinAnimationAt(symPos, symbol, finishedCallback);
	}

	public removeIdleAnimationAt(symPos: SymbolPosition): void {
		const symbol = (this._reels[symPos.x] as GameAvalancheReel).getSymbolFromPosition(symPos.y);
		symbol.removeIdleAnimation();
		symbol.visible = false;
	}

	public startBonusIdleAnimationAt(symPos: SymbolPosition): void {
		const symbol = (this._reels[symPos.x] as GameAvalancheReel).getSymbolFromPosition(symPos.y);
		symbol.visible = true;
		symbol.playScatterBonusIdleAnimation(3);
	}

	public removeRespinSymbolsFromReels(symPositions: SymbolPosition[]): void {
		for (let reel: number = 0; reel < ReelConstants.REELS_AMOUNT; reel++) {
			(this._reels[reel] as GameAvalancheReel).removeRespinSymbol(this.getAllPositionsFromReel(symPositions, reel));
		}
	}

	public dropNew(reels: GameSymbol[][], rowsToShow: number): void {
		this._currentScatterIndex = 0;
		this._reelsWithScatters = [];

		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			(this._reels[reelIndex] as GameAvalancheReel).moveSymbolsFromOversizedToReelContainer();
			this._reels[reelIndex].makeCleanup();
			this._reels[reelIndex].setRowsToShow(rowsToShow);
			this._reels[reelIndex].dropSymbols(reels[reelIndex]);
		}

		this.fixSymbolsVisibilityBeforeNewDrop();
	}

	public fixSymbolsVisibilityBeforeNewDrop(): void {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			(this._reels[reelIndex] as GameAvalancheReel).fixSymbolsBeforeNewDrop();
		}
	}

	public fixSymbolsVisibilityDuringAvalanches(): void {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			(this._reels[reelIndex] as GameAvalancheReel).fixSymbolsBeforeNewDrop();
		}
	}

	protected getBNSymbolsCountFromReel(symbols: GameSymbol[]): number {
		let bnSymbolCount: number = 0;

		for (const sym of symbols) {
			if ((sym.sym === Symbols.BN || sym.sym === Symbols.TOP_BN) && sym.position.y >= 3) {
				bnSymbolCount++;
			}
		}

		return bnSymbolCount;
	}

	protected getBNSymbolsCountFromAvalanche(symbols: AvalancheNewSymbol[]): number {
		let bnSymbolCount: number = 0;

		for (const sym of symbols) {
			if ((sym.symbol === Symbols.BN || sym.symbol === Symbols.TOP_BN) && sym.row >= 3) {
				bnSymbolCount++;
			}
		}

		return bnSymbolCount;
	}

	protected setupReelSetMaskForSpinning(): void {
		/*if (this.resources.hasMajorPerformanceCaveat) {
			this._currentMask = null;

			return;
		} - we can't not have mask in this game, so fuck those cheap devices */

		this._currentMask = new PIXI.Graphics();

		const scale: Scale = this.layout.getScale('BOTTOM_REEL_MASK');
		const noScaleSize: ObjectSize = this.layout.getSize('BOTTOM_REEL_MASK');
		const maskSize: ObjectSize = {width: noScaleSize.width * scale.x, height: noScaleSize.height * scale.y};
		const maskPos: Position = this.layout.getAbsolutePosition('BOTTOM_REEL_MASK');

		this._currentMask.drawRect(0, 0, 1, 1);

		this._currentMask.x = maskPos.x - maskSize.width / 2;
		this._currentMask.y = maskPos.y - maskSize.height / 2;
		this._currentMask.width = maskSize.width;
		this._currentMask.height = maskSize.height;
	}

	protected hasAnyReelsToFillVoid(): boolean {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			if ((this._reels[reelIndex] as GameAvalancheReel).hasAnythingToDropFillingTheVoid) {
				return true;
			}
		}

		return false;
	}

	public fillVoidAfterRemoval(): void {
		super.fillVoidAfterRemoval();

		this.emit(GameAvalancheReelsView.FILLING_VOIDS_STARTED);

		this.fixSymbolsVisibilityDuringAvalanches();
	}

	public addWinIndicationToSymbols(winLines: PrototypeWinLine[]): void {

		// Sort win lines
		winLines.sort((a: GamePrototypeWinLine, b: GamePrototypeWinLine): number => {
			// --TEMP FIX. Shouldn't be needed once parsing is working
			a.isRightToLeft = a.winPositions[0].x > a.winPositions[1].x;
			b.isRightToLeft = b.winPositions[0].x > b.winPositions[1].x;
			// --

			const aVal: number = a.isRightToLeft ? 1 : 2;
			const bVal: number = b.isRightToLeft ? 1 : 2;

			return bVal - aVal;
		});

		let totalTimerDuration: number = 0;

		for (const wl of winLines) {
			const isWinLineRightToLeft = (wl as GamePrototypeWinLine).isRightToLeft;
			let animationCounter: number = 0;
			for (const pos of wl.winPositions) {
				animationCounter++;
				this.timeUtils.createTimer(() => {
					if (this.isWinLineStarted(pos, isWinLineRightToLeft)) {
						this.emit(GameAvalancheReelsView.INITIAL_WIN_INDICATION_WINLINE);
					}
					(this._reels[pos.x] as GameAvalancheReel).addWinIndicationAt(pos, isWinLineRightToLeft);
				}, totalTimerDuration + animationCounter * TimingConstants.WIN_LINE_STAGGER_DELAY);
			}

			totalTimerDuration += ReelConstants.REELS_AMOUNT * TimingConstants.WIN_LINE_STAGGER_DELAY + TimingConstants.WIN_LINE_DELAY_BEFORE_NEXT_LINE;
		}

		const animDuration: number = this.resources.getSpine('INITIAL_WIN.json').findAnimation('EXIT').duration * 1000;
		totalTimerDuration += animDuration - TimingConstants.WIN_LINE_DELAY_BEFORE_NEXT_LINE;

		this.timeUtils.createTimer(() => {
			this.removeAllWinIndications();
			this.emit(GameAvalancheReelsView.INITIAL_WIN_INDICATION_FINISHED);
		}, totalTimerDuration);

		this.emit(GameAvalancheReelsView.INITIAL_WIN_INDICATION_STARTED, totalTimerDuration);
	}

	public removeAllWinIndications(): void {
		for (const reel of this._reels) {
			(reel as GameAvalancheReel).removeAllWinIndications();
		}
	}

	protected setOversizedLayer(): void {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			(this._reels[reelIndex] as GameAvalancheReel).setOversizedLayer(this._oversizedLayer);
		}
	}

	protected isWinLineStarted(pos: SymbolPosition, isWinLineRightToLeft: boolean): boolean {
		return (!isWinLineRightToLeft && pos.x === 0) || (isWinLineRightToLeft && pos.x === 4);
	}

	public setReelsContainerAlphaToZero(): void {
		this.container.alpha = 0;
	}

	public playReelContainerTransition(): void {
		const appearTween: Tween = new Tween(this.container)
			.to({alpha: 1.0}, BackgroundView.TRANSFER_TIME)
			.start();

		this.updateManager.add(appearTween);
	}
}



// WEBPACK FOOTER //
// ./src/avalanche/GameAvalancheReelsView.ts